import { Activity, UserStats } from '@/types';
import { db } from '@/utils/database';
import { activityManager } from '@/utils/activityManager';
import { Header } from '@/components/Header';
import { StatsDashboard } from '@/components/StatsDashboard';
import { ActivityCard } from '@/components/ActivityCard';
import { ActivityForm } from '@/components/ActivityForm';

export class App {
  private element: HTMLElement;
  private header: Header;
  private statsDashboard: StatsDashboard;
  private activityForm: ActivityForm | null = null;
  private activities: Activity[] = [];
  private completedToday: string[] = [];
  private userStats: UserStats = {
    totalPoints: 0,
    totalActivities: 0,
    longestStreak: 0,
    currentStreak: 0,
    activitiesCompleted: 0,
    level: 1
  };

  constructor() {
    this.element = document.createElement('div');
    this.element.className = 'app';
    
    this.header = new Header(this.userStats);
    this.statsDashboard = new StatsDashboard(this.userStats, this.activities);
  }

  async initialize(): Promise<void> {
    await db.init();
    await this.loadData();
    this.render();
  }

  private async loadData(): Promise<void> {
    this.activities = await activityManager.getActiveActivities();
    this.completedToday = await activityManager.getCompletedActivitiesToday();
    this.userStats = await activityManager.getUserStats();
    this.userStats.totalActivities = this.activities.length;
    
    this.header.updateStats(this.userStats);
    this.statsDashboard.updateStats(this.userStats, this.activities);
  }

  private render(): void {
    this.element.innerHTML = '';
    
    this.element.appendChild(this.header.getElement());
    
    const main = document.createElement('main');
    main.className = 'main-content';
    main.innerHTML = `
      <div class="container" style="padding: 24px 20px;">
        <div class="stats-container mb-6"></div>
        
        <div class="activities-header flex items-center justify-between mb-6">
          <h2 class="font-bold text-2xl" style="color: var(--text-primary);">Your Activities</h2>
          <button class="btn btn-primary add-activity-btn">
            ➕ Add Activity
          </button>
        </div>
        
        <div class="activities-grid">
          ${this.activities.length === 0 ? this.renderEmptyState() : ''}
        </div>
      </div>
    `;

    const statsContainer = main.querySelector('.stats-container') as HTMLElement;
    statsContainer.appendChild(this.statsDashboard.getElement());

    const activitiesGrid = main.querySelector('.activities-grid') as HTMLElement;
    if (this.activities.length > 0) {
      activitiesGrid.className = 'activities-grid grid grid-2 gap-4';
      this.renderActivities(activitiesGrid);
    }

    this.element.appendChild(main);
    this.attachEventListeners();
  }

  private renderEmptyState(): string {
    return `
      <div class="empty-state text-center py-12">
        <div class="text-6xl mb-4">🎯</div>
        <h3 class="font-bold text-xl mb-2" style="color: var(--text-primary);">No Activities Yet</h3>
        <p class="mb-6" style="color: var(--text-secondary);">Create your first activity to start building streaks!</p>
        <button class="btn btn-primary add-activity-btn">
          ➕ Add Your First Activity
        </button>
      </div>
    `;
  }

  private async renderActivities(container: HTMLElement): Promise<void> {
    container.innerHTML = '';
    
    for (const activity of this.activities) {
      const streak = await activityManager.getActivityStreak(activity.id);
      const isCompletedToday = this.completedToday.includes(activity.id);
      
      const activityCard = new ActivityCard(activity, streak, isCompletedToday, {
        onComplete: (activityId) => this.handleCompleteActivity(activityId),
        onEdit: (activity) => this.handleEditActivity(activity),
        onDelete: (activityId) => this.handleDeleteActivity(activityId)
      });
      
      container.appendChild(activityCard.getElement());
    }
  }

  private attachEventListeners(): void {
    const addActivityBtns = this.element.querySelectorAll('.add-activity-btn');
    addActivityBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        this.showActivityForm();
      });
    });
  }

  private showActivityForm(editingActivity?: Activity): void {
    this.activityForm = new ActivityForm({
      onSubmit: (activityData) => this.handleSubmitActivity(activityData, editingActivity),
      onCancel: () => this.hideActivityForm()
    });

    if (editingActivity) {
      this.activityForm.setEditingActivity(editingActivity);
    }

    document.body.appendChild(this.activityForm.getElement());
  }

  private hideActivityForm(): void {
    if (this.activityForm) {
      document.body.removeChild(this.activityForm.getElement());
      this.activityForm = null;
    }
  }

  private async handleSubmitActivity(
    activityData: Omit<Activity, 'id' | 'createdAt'>,
    editingActivity?: Activity
  ): Promise<void> {
    try {
      if (editingActivity) {
        const updatedActivity: Activity = {
          ...editingActivity,
          ...activityData
        };
        await activityManager.updateActivity(updatedActivity);
      } else {
        await activityManager.createActivity(activityData);
      }
      
      await this.loadData();
      this.render();
      this.hideActivityForm();
    } catch (error) {
      console.error('Error saving activity:', error);
      alert('Failed to save activity. Please try again.');
    }
  }

  private async handleCompleteActivity(activityId: string): Promise<void> {
    try {
      await activityManager.completeActivity(activityId);
      await this.loadData();
      this.render();
      
      const activity = this.activities.find(a => a.id === activityId);
      if (activity) {
        this.showCompletionMessage(activity);
      }
    } catch (error) {
      console.error('Error completing activity:', error);
      alert('Failed to complete activity. Please try again.');
    }
  }

  private handleEditActivity(activity: Activity): void {
    this.showActivityForm(activity);
  }

  private async handleDeleteActivity(activityId: string): Promise<void> {
    try {
      await activityManager.deleteActivity(activityId);
      await this.loadData();
      this.render();
    } catch (error) {
      console.error('Error deleting activity:', error);
      alert('Failed to delete activity. Please try again.');
    }
  }

  private showCompletionMessage(activity: Activity): void {
    const message = document.createElement('div');
    message.className = 'completion-message';
    message.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #10b981, #059669);
      color: white;
      padding: 16px 24px;
      border-radius: 12px;
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
      z-index: 1000;
      animation: slideInRight 0.3s ease;
    `;
    
    message.innerHTML = `
      <div class="font-semibold">🎉 Great job!</div>
      <div class="text-sm">You completed "${activity.name}" and earned ${activity.pointsPerCompletion} points!</div>
    `;

    document.body.appendChild(message);
    
    setTimeout(() => {
      message.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => {
        if (document.body.contains(message)) {
          document.body.removeChild(message);
        }
      }, 300);
    }, 3000);
  }

  public getElement(): HTMLElement {
    return this.element;
  }
}